import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import { AuthState, User, LoginCredentials } from '../../types'
import * as authAPI from '../../api/auth'
import api from '../../api/client'

const getStoredUser = (): User | null => {
  try {
    const userJson = localStorage.getItem('user')
    return userJson ? JSON.parse(userJson) : null
  } catch {
    return null
  }
}

const initialState: AuthState = {
  user: getStoredUser(),
  token: localStorage.getItem('token'),
  isLoading: false,
  isAuthenticated: !!localStorage.getItem('token'),
  error: null,
}

/**
 * Legacy login with email/password
 * @deprecated Use Azure SSO for frontend authentication
 */
export const loginUser = createAsyncThunk(
  'auth/login',
  async (credentials: LoginCredentials, { rejectWithValue }) => {
    try {
      const response = await authAPI.login(credentials)
      localStorage.setItem('token', response.token)
      localStorage.setItem('refresh_token', response.refresh)
      return response
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Login failed')
    }
  }
)

/**
 * SSO Login - Exchange Azure AD id_token with backend
 * This is called automatically by MsalProvider after Azure AD redirect
 */
export const ssoLogin = createAsyncThunk(
  'auth/ssoLogin',
  async (idToken: string, { rejectWithValue }) => {
    try {
      const response = await authAPI.azureBridge(idToken)
      
      // For SSO, we use session-based auth (cookie set by backend)
      // Set a marker token to track authentication state in Redux
      localStorage.setItem('token', 'sso-session')
      
      // Convert bridge response to User format
      const nameParts = response.user.display_name.split(' ')
      const user: Partial<User> = {
        email: response.user.email,
        first_name: nameParts[0] || '',
        last_name: nameParts.slice(1).join(' ') || '',
      }
      
      localStorage.setItem('user', JSON.stringify(user))

      // If the bridge returned a csrf_token, set it as a cookie for the SPA
      // to pick up and include in subsequent mutating requests. We do NOT
      // persist this token in localStorage — only set a cookie (double-submit).
      if ((response as any).csrf_token) {
        const csrfToken = (response as any).csrf_token
        try {
          // Use Secure attribute only if page is served over HTTPS
          const secure = window.location.protocol === 'https:'
          const sameSite = 'Lax'
          // Build cookie string; keep it readable and minimal
          const cookieParts = [`csrftoken=${csrfToken}`, 'path=/', `SameSite=${sameSite}`]
          if (secure) cookieParts.push('Secure')
          // Do NOT set HttpOnly - SPA needs to read it.
          document.cookie = cookieParts.join('; ')

          // Also set axios default header so subsequent requests from the
          // current JS runtime include the CSRF header immediately.
          try {
            // Set default common header for axios so all subsequent requests
            // include the CSRF token by default
            api.defaults.headers.common = api.defaults.headers.common || {}
            api.defaults.headers.common['X-CSRFToken'] = csrfToken
          } catch (e) {
            // Ignore if api not available for some reason
          }
        } catch (e) {
          // Ignore cookie set errors — SPA will still attempt to use server-set cookie
        }
      }
      
      return {
        user,
        token: 'sso-session',
        created: response.created,
      }
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || 
        error.response?.data?.message || 
        'SSO authentication failed'
      )
    }
  }
)

export const logoutUser = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await authAPI.logout()
      localStorage.removeItem('token')
      localStorage.removeItem('refresh_token')
      return null
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Logout failed')
    }
  }
)

export const getCurrentUser = createAsyncThunk(
  'auth/getCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authAPI.getCurrentUser()
      return response.data
    } catch (error: any) {
      localStorage.removeItem('token')
      return rejectWithValue(error.response?.data?.message || 'Failed to get user')
    }
  }
)

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    setToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload
      state.isAuthenticated = true
      localStorage.setItem('token', action.payload)
    },
    clearAuth: (state) => {
      state.user = null
      state.token = null
      state.isAuthenticated = false
      localStorage.removeItem('token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('user')
    },
    updateUserProfile: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload }
        localStorage.setItem('user', JSON.stringify(state.user))
      }
    },
  },
  extraReducers: (builder) => {
    // Legacy Login (email/password)
    builder
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false
        state.user = action.payload.user
        state.token = action.payload.token
        state.isAuthenticated = true
        state.error = null
        localStorage.setItem('user', JSON.stringify(action.payload.user))
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
        state.isAuthenticated = false
      })

    // SSO Login (Azure AD)
    builder
      .addCase(ssoLogin.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(ssoLogin.fulfilled, (state, action) => {
        state.isLoading = false
        state.user = action.payload.user as User
        state.token = action.payload.token
        state.isAuthenticated = true
        state.error = null
      })
      .addCase(ssoLogin.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
        state.isAuthenticated = false
      })

    // Logout
    builder
      .addCase(logoutUser.pending, (state) => {
        state.isLoading = true
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.isLoading = false
        state.user = null
        state.token = null
        state.isAuthenticated = false
        state.error = null
        localStorage.removeItem('token')
        localStorage.removeItem('refresh_token')
        localStorage.removeItem('user')
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })

    // Get Current User
    builder
      .addCase(getCurrentUser.pending, (state) => {
        state.isLoading = true
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.isLoading = false
        state.user = action.payload
        state.isAuthenticated = true
        state.error = null
        localStorage.setItem('user', JSON.stringify(action.payload))
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
        state.isAuthenticated = false
        state.user = null
        state.token = null
        localStorage.removeItem('token')
        localStorage.removeItem('user')
      })
  },
})

export const { clearError, setToken, clearAuth, updateUserProfile } = authSlice.actions
export default authSlice.reducer
